import React, { useState, useEffect } from 'react';
import { SettingCard } from '../../components/SettingCards/SettingCard/SettingCard';
import { SettingCardContent } from '../../components/SettingCards/SettingCard/SettingCardContent/SettingCardContent';
import { Dropdown, DropdownOption } from '../../components/Dropdown/Dropdown';
import { RadioSetting } from '../../components/SettingCards/RadioSetting/RadioSetting';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { SettingsLogger, logConstants, AotCategory } from '../../utils/SettingsLogger';
import './PermissionsSetting.css';
import {
	getSettingConfig,
	PermissionsSettingGroupConfig,
} from '../../config/SettingsDefinitions';

export interface PermissionsSettingProps {
	settingId: string;
}

export interface PermissionsSettingValue {
	storeOptions: DropdownOption[];
	[key: string]: any; // Dynamic subsetting values with permission
}

export interface PermissionGroupProps {
	title: string;
	radioOptions: Array<{ value: string; label: string }>;
	selectedPermission: string;
	onPermissionChange: (value: string) => void;
	disabled?: boolean;
}

const PermissionGroup: React.FC<PermissionGroupProps> = ({
	title,
	radioOptions,
	selectedPermission,
	onPermissionChange,
	disabled = false,
}) => {
	return (
		<div className="permission-group">
			<div className="permission-group-header">
				<h4 className="permission-group-title">{title}</h4>
			</div>
			<div className="permission-radio-section">
				<RadioSetting
					title=""
					options={radioOptions}
					selectedValue={selectedPermission}
					onChange={onPermissionChange}
					settingId={`permissions-${title.toLowerCase().replace(/\s+/g, '-')}`}
					disabled={disabled}
					card={false}
				/>
			</div>
		</div>
	);
};

export const PermissionsSetting: React.FC<PermissionsSettingProps> = ({ settingId }) => {
	const [settingValue, setSettingValue] = useState<PermissionsSettingValue | null>(null);
	const [selectedStore, setSelectedStore] = useState<string>('');
	const [disabled, setDisabled] = useState<boolean>(false);

	useEffect(() => {
		loadPermissionsSetting();
	}, []);

	const loadPermissionsSetting = async () => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			const config = getSettingConfig(settingId) as PermissionsSettingGroupConfig;

			const response = await bridge.getSetting({
				settingKey: settingId,
			});

			if (response.status === 0 && response.data?.settingValue) {
				const parsedValue =
					typeof response.data.settingValue === 'string'
						? JSON.parse(response.data.settingValue)
						: response.data.settingValue;

				const storeOptions = parsedValue.storeOptions.map(
					(store: string | DropdownOption) =>
						typeof store === 'string' ? { value: store, label: store } : store
				);

				const firstStore = storeOptions[0]?.value || 'global';

				const subsettingKeys = Object.keys(config.subsettings);
				const permissionPromises = subsettingKeys.map((subsettingKey) =>
					fetchSubsettingValue(subsettingKey, firstStore)
				);

				const permissions = await Promise.all(permissionPromises);

				const initialValue: PermissionsSettingValue = { storeOptions };

				subsettingKeys.forEach((subsettingKey, index) => {
					initialValue[subsettingKey] = {
						permission:
							permissions[index] ||
							config.subsettings[subsettingKey].options[0]?.value ||
							'',
					};
				});

				setSelectedStore(firstStore);
				setSettingValue(initialValue);
				setDisabled(!response.data?.enabled || false);
			} else {
				SettingsLogger.getInstance().write(
					logConstants.ERROR,
					'Permissions setting not found'
				);
			}
		} catch (err) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to load permissions setting: ${err}`,
				AotCategory.Error,
				`Failed to load permissions setting: ${err}`
			);
		}
	};

	const onSharedStoreChange = async (storeValue: string) => {
		setSelectedStore(storeValue);

		const config = getSettingConfig(settingId) as PermissionsSettingGroupConfig;
		const subsettingKeys = Object.keys(config.subsettings);
		const permissionPromises = subsettingKeys.map((subsettingKey) =>
			fetchSubsettingValue(subsettingKey, storeValue)
		);

		const permissions = await Promise.all(permissionPromises);

		setSettingValue((prevValue) => {
			if (!prevValue) return prevValue;
			const updated = { ...prevValue };
			subsettingKeys.forEach((subsettingKey, index) => {
				if (permissions[index] !== null) {
					updated[subsettingKey] = { permission: permissions[index] };
				}
			});
			return updated;
		});
	};

	const saveSubsetting = async (
		subsettingKey: string,
		store: string,
		permission: string
	) => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			const storeValue = store || 'global';
			await bridge.setSetting({
				settingKey: subsettingKey,
				settingValue: JSON.stringify({ store: storeValue, permission }),
			});
		} catch (err) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to save subsetting: ${err}`,
				AotCategory.Error,
				`Failed to save subsetting: ${err}`
			);
		}
	};

	const fetchSubsettingValue = async (
		subsettingKey: string,
		storeValue: string
	): Promise<string | null> => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			const response = await bridge.getSetting({
				settingKey: subsettingKey,
			});

			if (response.status === 0 && response.data?.settingValue) {
				const storePermissions =
					typeof response.data.settingValue === 'string'
						? JSON.parse(response.data.settingValue)
						: response.data.settingValue;

				return storePermissions[storeValue] || null;
			}
			return null;
		} catch (err) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to fetch subsetting value: ${err}`,
				AotCategory.Error,
				`Failed to fetch subsetting value: ${err}`
			);
			return null;
		}
	};

	const config = getSettingConfig(settingId) as PermissionsSettingGroupConfig;

	if (!settingValue) {
		return null;
	}

	return (
		<SettingCard
			title={config.title}
			description={config.description}
			settingId={settingId}
			disabled={disabled}
			showContentSeparator={config.showContentSeparator ?? false}
			content={
				<SettingCardContent>
					<div className="permissions-container">
						<div className="permissions-store-section">
							<Dropdown
								options={settingValue.storeOptions}
								value={selectedStore}
								onChange={onSharedStoreChange}
								label="Select a store"
								showLabel={true}
								disabled={disabled}
								className="permission-dropdown"
							/>
						</div>
						{Object.entries(config.subsettings).map(
							([subsettingKey, subsettingConfig]) => {
								const subsettingValue = settingValue[subsettingKey];

								if (!subsettingValue) {
									return null;
								}

								return (
									<PermissionGroup
										key={subsettingKey}
										title={subsettingConfig.title}
										radioOptions={subsettingConfig.options}
										selectedPermission={subsettingValue.permission}
										onPermissionChange={async (permission: string) => {
											setSettingValue((prevValue) => {
												if (!prevValue) return prevValue;
												return {
													...prevValue,
													[subsettingKey]: { permission },
												};
											});
											await saveSubsetting(subsettingKey, selectedStore, permission);
										}}
										disabled={disabled}
									/>
								);
							}
						)}
					</div>
				</SettingCardContent>
			}
		/>
	);
};
